import { Box, Flex, Image, Text, VStack } from "@chakra-ui/react";
import { YoutubeIframePlayer } from "../news/components/YoutubeIframePlayer";
import { FeedVideoItem } from "./types";

interface FeedLongVideoCardProps {
  item: FeedVideoItem;
  isActive: boolean;
}

/**
 * Compact card for long-form landscape videos. The iframe mounts only when
 * active to avoid burning CPU on offscreen players; otherwise we render the
 * still thumbnail with a play affordance.
 */
export function FeedLongVideoCard({ item, isActive }: FeedLongVideoCardProps) {
  const thumb =
    item.thumbnail_url ??
    `https://i.ytimg.com/vi/${item.youtube_id}/hqdefault.jpg`;
  const embedSrc =
    `https://www.youtube.com/embed/${item.youtube_id}` +
    `?modestbranding=1&rel=0&playsinline=1`;

  return (
    <Flex
      w="100%"
      h="100%"
      align="center"
      justify="center"
      px={{ base: 4, md: 8 }}
      py={2}
      bgGradient="linear(180deg, rgba(20,18,32,1) 0%, rgba(8,8,16,1) 100%)"
    >
      <Box
        w="100%"
        maxW={{ base: "100%", md: "560px" }}
        bg="rgba(255,255,255,0.04)"
        border="1px solid rgba(255,255,255,0.08)"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0 24px 60px rgba(0,0,0,0.5)"
      >
        <Box w="100%" pt="56.25%" position="relative" bg="black">
          {isActive ? (
            <YoutubeIframePlayer position="absolute" inset={0}>
              <iframe
                src={embedSrc}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title={item.title}
              />
            </YoutubeIframePlayer>
          ) : (
            <>
              <Image
                src={thumb}
                alt={item.title}
                position="absolute"
                inset={0}
                w="100%"
                h="100%"
                objectFit="cover"
              />
              <Box
                position="absolute"
                inset={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  w="56px"
                  h="56px"
                  borderRadius="full"
                  bg="rgba(0,0,0,0.7)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 8px 24px rgba(0,0,0,0.4)"
                >
                  <Box
                    as="span"
                    display="inline-block"
                    w="0"
                    h="0"
                    ml="4px"
                    borderTop="10px solid transparent"
                    borderBottom="10px solid transparent"
                    borderLeft="16px solid white"
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
        <VStack align="stretch" spacing={1} px={4} py={3} color="white">
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
            opacity={0.7}
          >
            Watch
          </Text>
          <Text fontSize="md" fontWeight="bold" lineHeight="1.2" noOfLines={2}>
            {item.title}
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}
